import { useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { apiError } from "@/lib/api";
import { GraduationCap, CircleNotch, GoogleLogo } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Logo } from "@/components/Logo";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function googleLogin() {
  const redirectUrl = window.location.origin + "/dashboard";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      await login(email, password);
      toast.success("¡Bienvenido de vuelta!");
      navigate(location.state?.from || "/dashboard");
    } catch (e) {
      setErr(apiError(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between bg-gradient-to-br from-blue-50 via-white to-blue-100 text-foreground p-12 border-r border-border">
        <Link to="/" className="flex items-center">
          <Logo className="h-9" />
        </Link>
        <div>
          <h2 className="font-display text-4xl font-800 tracking-tight leading-none text-foreground">
            Capacitación profesional<br />con certificación real.
          </h2>
          <p className="text-muted-foreground mt-4 max-w-md leading-relaxed">
            Accede a tus cursos, evaluaciones y certificados. Formación reconocida para el mundo laboral chileno.
          </p>
        </div>
        <p className="text-xs text-muted-foreground">© 2026 NovaCert OTEC</p>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12 bg-background">
        <div className="w-full max-w-sm animate-fade-up">
          <Link to="/" className="lg:hidden flex items-center mb-8">
            <Logo className="h-9" />
          </Link>
          <p className="text-xs uppercase tracking-overline text-primary mb-2">Ingreso</p>
          <h1 className="font-display text-3xl font-800 tracking-tight mb-6">Inicia sesión</h1>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="email">Correo electrónico</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                required data-testid="login-email" className="mt-1.5" placeholder="tu@correo.cl" />
            </div>
            <div>
              <div className="flex justify-between items-center">
                <Label htmlFor="password">Contraseña</Label>
                <Link to="/forgot-password" className="text-xs text-primary hover:underline" data-testid="login-forgot-link">
                  ¿Olvidaste tu contraseña?
                </Link>
              </div>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                required data-testid="login-password" className="mt-1.5" placeholder="••••••••" />
            </div>
            {err && <p className="text-sm text-destructive" data-testid="login-error">{err}</p>}
            <Button type="submit" disabled={loading} data-testid="login-submit"
              className="w-full bg-primary hover:bg-blue-600 text-white">
              {loading ? <CircleNotch className="animate-spin" /> : "Ingresar"}
            </Button>
          </form>
          <div className="flex items-center gap-3 my-5">
            <div className="h-px bg-border flex-1" /><span className="text-xs text-muted-foreground">o</span><div className="h-px bg-border flex-1" />
          </div>
          <Button type="button" variant="outline" onClick={googleLogin} data-testid="login-google"
            className="w-full gap-2">
            <GoogleLogo size={18} weight="bold" /> Continuar con Google
          </Button>
          <p className="text-sm text-muted-foreground mt-6 text-center">
            ¿No tienes cuenta? <Link to="/register" className="text-primary font-medium hover:underline">Regístrate</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
